import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import {
  FILTER_FEATURE,
  PARTNER_MANAGEMENT,
  STORE_API,
  USER_CHAIN,
} from "api/ApiPath";
import { StoreListRequestModel } from "api/merchantRepository/filterFeature/model/storeList/StoreListRequestModel";
import { StoreListResponseModel } from "api/merchantRepository/filterFeature/model/storeList/StoreListResponseModel";
import { PartnerManagementRequestModel } from "api/repositories/partnerManagement/model/PartnerManagementRequestModel";
import { PartnerManagementResponseModel } from "api/repositories/partnerManagement/model/PartnerManagementResponseModel";
import { StoreResponseModel } from "features/storeManagement/data/model/StoreResponseModel";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { B2CUserInjection } from "features/b2cUserManagement/di/B2CUserInjection";
import { B2CDetailUserEntity } from "features/b2cUserManagement/domain/entities/B2CDetailUserEntity";
import { B2CUserActionRequestEntity } from "features/b2cUserManagement/domain/entities/B2CUserActionRequestEntity";
import { B2CUserRequestEntity } from "features/b2cUserManagement/domain/entities/B2CUserRequestEntity";
import { B2CUserResponseEntity } from "features/b2cUserManagement/domain/entities/B2CUserResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { UserTypeEnum } from "utils/enums/UserTypeEnum";
import { FeatureFormat } from "utils/format/FeatureFormat";
import { strings } from "utils/localizedStrings";

class UserChainInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public storeLoading = new BehaviorSubject<string>("");
  public fetchUserChain = new BehaviorSubject<B2CUserRequestEntity>({});
  public fetchStore = new BehaviorSubject<{
    params?: StoreListRequestModel;
    type?: "list" | "modal";
    typeOfUser?: UserTypeEnum;
  }>({});
  public fetchPartner = new BehaviorSubject<PartnerManagementRequestModel>({});
  public fetchDetail = new BehaviorSubject<B2CUserRequestEntity>({});
  public fetchActions = new BehaviorSubject<{
    body?: B2CUserActionRequestEntity;
    action?: "create" | "update" | "delete";
  }>({});

  [Symbol.dispose](): void {
    this.loading.complete();
    this.storeLoading.complete();
    this.fetchUserChain.complete();
    this.fetchStore.complete();
    this.fetchPartner.complete();
    this.fetchDetail.complete();
    this.fetchActions.complete();
  }
}

class UserChainOutput implements Disposable {
  public savedUserChain = new BehaviorSubject<Array<B2CUserResponseEntity>>([]);
  public pagination = new BehaviorSubject<Paging>({});
  public savedStore = new BehaviorSubject<Array<StoreListResponseModel>>([]);
  public savedStoreForModal = new BehaviorSubject<
    Array<StoreListResponseModel>
  >([]);
  public savedPartner = new BehaviorSubject<
    Array<PartnerManagementResponseModel>
  >([]);
  public savedDetail = new BehaviorSubject<B2CDetailUserEntity>({});
  public onError = new BehaviorSubject<Error>({});
  public onUpdateSuccess = new BehaviorSubject<boolean>(false);
  public onActionsSuccess = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.pagination.complete();
    this.savedUserChain.complete();
    this.savedStoreForModal.complete();
    this.savedStore.complete();
    this.savedPartner.complete();
    this.savedDetail.complete();
    this.onError.complete();
    this.onUpdateSuccess.complete();
    this.onActionsSuccess.complete();
  }
}

@injectable()
export class UserChainManagementViewModel extends BaseViewModel<
  UserChainInput,
  UserChainOutput
> {
  constructor() {
    super(new UserChainInput(), new UserChainOutput());
  }
  private useCase = B2CUserInjection.getUseCase();

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: B2CUserRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : `createdAt DESC`;

    const query = {
      ...prevParams,
      ...rest,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchUserChain, async (params) => {
      try {
        this.input.loading.next(true);
        const res = await this.useCase?.getList(params, USER_CHAIN.LIST);
        if (res?.data) {
          this.output.savedUserChain.next(res?.data);
          this.output.pagination.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        const res = await this.useCase?.getDetail(
          { phone: params?.phone },
          USER_CHAIN.DETAIL,
        );
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchPartner, async (params) => {
      await FeatureFormat.fetchFilterList<
        PartnerManagementResponseModel,
        PartnerManagementRequestModel
      >({
        dataState: (value) => this.output.savedPartner.next(value),
        path: PARTNER_MANAGEMENT.LIST,
        isForceRefresh: true,
        params,
        savedKey: CacheKeyEnum.b2cPartner,
      });
    });

    this.skipFirstInitial(this.input.fetchStore, async (props) => {
      if (props?.typeOfUser === UserTypeEnum.chain) {
        this.input.storeLoading.next(props?.type);
        return FeatureFormat.fetchFilterList<
          StoreResponseModel,
          StoreListRequestModel
        >({
          dataState: (value) =>
            props?.type === "list"
              ? this.output.savedStore.next(value)
              : this.output.savedStoreForModal.next(value),
          path:
            props?.type === "list" ? FILTER_FEATURE.STORE_LIST : STORE_API.LIST,
          isForceRefresh: true,
          isNeedFeature: false,
          params: props?.params,
        })?.finally(() => this.input.storeLoading.next(""));
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (props) => {
      let { action, body } = props;
      switch (action) {
        case "create":
          try {
            const res = await this.useCase?.createUser(body, USER_CHAIN.CREATE);
            if (res?.data) {
              this.output.onActionsSuccess.next(
                strings.GlobalMessage.CreateSuccessfully,
              );
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          }
          break;
        case "update":
          try {
            const res = await this.useCase?.updateUser(body, USER_CHAIN.UPDATE);
            if (res?.data) {
              this.output.onUpdateSuccess.next(true);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          }
          break;
        case "delete":
          try {
            this.input.loading.next(true);
            const res = await this.useCase?.deleteUser(
              { phone: body?.phone },
              USER_CHAIN.DELETE,
            );
            if (res?.data) {
              this.output.onActionsSuccess.next(
                strings.GlobalMessage.DeleteSuccessfully,
              );
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.loading.next(false);
          }
          break;
      }
    });
  }
}
